import { activeBirthEvents } from './birthCalculations';
import type { ClinicalEpisode, ClinicalEvent } from './types';
export interface CourseEncounter {
  id: string;
  date: string;
  label: string;
  kind?: string;
}
export interface CourseCell {
  value: string;
  sourceIds: string[];
}
export interface CourseRow {
  encounterId: string;
  date: string;
  label: string;
  subjectId: string;
  subjectLabel: string;
  subjectType: string;
  ageDays: number | null;
  cells: Record<string, CourseCell>;
}
export const maternalCourseColumns = [
  'BP',
  'Pulse',
  'Temperature',
  'Fundus',
  'Lochia',
  'Healing',
  'Breasts / nipples',
  'Bladder / bowel',
  'Pain',
  'Mood / EPDS',
  'Contraception',
  'Notes',
] as const;
export const newbornCourseColumns = [
  'Weight',
  'Weight change',
  'Feeding',
  'Voids / stools',
  'Jaundice',
  'Cord',
  'Temperature',
  'HR / RR',
  'Screens',
  'Exam',
  'Notes',
] as const;
const display = (value: unknown): string =>
  value === undefined || value === null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
export function epdsScore(items: unknown): { total: number; item10Positive: boolean } | null {
  return Array.isArray(items) && items.length === 10 && items.every((v) => Number.isInteger(v) && v >= 0 && v <= 3)
    ? { total: items.reduce((a, b) => a + b, 0), item10Positive: items[9] > 0 }
    : null;
}
/** Export-ready projection; every derived cell retains its contributing immutable event IDs. */
export function postpartumCourseRows(
  events: ClinicalEvent[],
  episode: ClinicalEpisode,
  encounters: CourseEncounter[],
  now = new Date().toISOString(),
  timeZone = 'UTC',
): CourseRow[] {
  const active = activeBirthEvents(events, episode.id, now) as ClinicalEvent[];
  const births = active.filter((e) => e.event_type === 'BIRTH');
  const firstBirth = births[0];
  const known = new Map(encounters.map((e) => [e.id, e]));
  for (const e of active.filter(
    (e) => e.event_type.startsWith('PP.') || e.event_type.startsWith('NB.') || e.event_type === 'SCREEN.EPDS',
  ))
    if (!known.has(e.encounter_id))
      known.set(e.encounter_id, { id: e.encounter_id, date: e.occurred_at, label: 'Documented postpartum visit' });
  const rows: CourseRow[] = [];
  for (const visit of [...known.values()].sort((a, b) => a.date.localeCompare(b.date))) {
    const visitEvents = active.filter((e) => e.encounter_id === visit.id);
    if (!visitEvents.length && !/(postpartum|newborn)/i.test(visit.kind ?? visit.label)) continue;
    if (
      births.some((b) => b.encounter_id === visit.id) &&
      !visitEvents.some((e) =>
        ['PP.COURSE_ASSESSMENT', 'NB.COURSE_ASSESSMENT', 'PP.EPDS', 'SCREEN.EPDS'].includes(e.event_type),
      )
    )
      continue;
    if (firstBirth && visit.date.slice(0, 10) < firstBirth.occurred_at.slice(0, 10)) continue;
    for (const subject of episode.subjects.filter((s) => s.subject_type !== 'FETUS')) {
      const observations = visitEvents.filter((e) => e.subject_id === subject.id);
      const birth =
        subject.subject_type === 'MOTHER' ? firstBirth : births.find((e) => e.subject_id === subject.fetus_subject_id);
      const cells: Record<string, CourseCell> = {};
      const add = (column: string, e: ClinicalEvent, value: string) => {
        if (!value) return;
        const previous = cells[column];
        cells[column] = {
          value: previous ? `${previous.value}; ${value}` : value,
          sourceIds: [...(previous?.sourceIds ?? []), e.id],
        };
      };
      for (const e of observations) {
        const p = e.payload,
          t = e.event_type;
        if (t === 'VITALS.BP') add('BP', e, `${p.systolic}/${p.diastolic} mmHg`);
        if (t === 'VITALS.PULSE') add(subject.subject_type === 'MOTHER' ? 'Pulse' : 'HR / RR', e, `HR ${p.rate}/min`);
        if (t === 'VITALS.RR') add('HR / RR', e, `RR ${p.rate}/min`);
        if (t === 'VITALS.TEMP') add('Temperature', e, `${p.value_c} °C`);
        if (t === 'PP.FUNDUS')
          add(
            'Fundus',
            e,
            Object.entries(p)
              .map(([k, v]) => `${k.replaceAll('_', ' ')}: ${display(v)}`)
              .join(', '),
          );
        if (t === 'PP.COURSE_ASSESSMENT')
          for (const [column, keys] of Object.entries({
            Lochia: ['lochia_color', 'lochia_amount', 'lochia_odor'],
            Healing: ['perineum_healing', 'incision_healing'],
            'Breasts / nipples': ['breasts', 'nipples'],
            'Bladder / bowel': ['bladder', 'bowel'],
            Pain: ['pain_score'],
            'Mood / EPDS': ['mood'],
            Contraception: ['contraception_plan'],
            Notes: ['notes'],
          }))
            add(
              column,
              e,
              keys
                .filter((k) => p[k] !== undefined)
                .map((k) => `${k.replaceAll('_', ' ')}: ${display(p[k])}`)
                .join(', '),
            );
        if (t === 'PP.EPDS' || t === 'SCREEN.EPDS') {
          const score = epdsScore(p.item_scores ?? p.items);
          if (score)
            add(
              'Mood / EPDS',
              e,
              `EPDS ${score.total}/30${score.item10Positive ? ' · Item 10 safety review required' : ''}`,
            );
        }
        if (t === 'NB.MEASUREMENTS' && typeof p.weight_g === 'number') {
          add('Weight', e, `${p.weight_g} g`);
          const baseline = active.find(
            (v) =>
              v.subject_id === subject.id &&
              v.event_type === 'NB.MEASUREMENTS' &&
              v.encounter_id === birth?.encounter_id &&
              typeof v.payload.weight_g === 'number' &&
              birth &&
              Date.parse(v.occurred_at) >= Date.parse(birth.occurred_at),
          );
          if (baseline && Number(baseline.payload.weight_g) > 0) {
            add(
              'Weight change',
              e,
              `${(((p.weight_g - Number(baseline.payload.weight_g)) / Number(baseline.payload.weight_g)) * 100).toFixed(1)}% from documented birth-encounter weight`,
            );
            cells['Weight change'].sourceIds.push(baseline.id);
          } else {
            add('Weight change', e, 'Birth-encounter weight not documented');
          }
        }
        if (t === 'NB.FEEDING')
          add(
            'Feeding',
            e,
            Object.entries(p)
              .map(([k, v]) => `${k.replaceAll('_', ' ')}: ${display(v)}`)
              .join(', '),
          );
        if (t === 'NB.COURSE_ASSESSMENT')
          for (const [column, keys] of Object.entries({
            Feeding: ['feeding_frequency_per24h', 'latch'],
            'Voids / stools': ['voids_per24h', 'stools_per24h'],
            Jaundice: ['jaundice_visual_zone', 'tcb_value', 'tcb_unit'],
            Cord: ['cord'],
            Screens: ['metabolic_screen_status', 'cchd_status', 'hearing_status'],
            Exam: ['exam_findings'],
            Notes: ['notes'],
          }))
            add(
              column,
              e,
              keys
                .filter((k) => p[k] !== undefined)
                .map((k) => `${k.replaceAll('_', ' ')}: ${display(p[k])}`)
                .join(', '),
            );
        if (t === 'NB.SCREEN') add('Screens', e, `${display(p.type)}: ${display(p.result)}`);
        if (t === 'NB.EXAM') add('Exam', e, display(p));
        if (t === 'NOTE.PROGRESS') add('Notes', e, display(p.text));
      }
      // A course row belongs to the visit date, not an unrelated earlier event entered in that encounter.
      const birthDate = birth
        ? new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
            new Date(birth.occurred_at),
          )
        : null;
      const age = birth
        ? /^\d{4}-\d{2}-\d{2}$/.test(visit.date)
          ? (Date.parse(visit.date) - Date.parse(birthDate!)) / 86400000
          : (Date.parse(visit.date) - Date.parse(birth.occurred_at)) / 86400000
        : null;
      rows.push({
        encounterId: visit.id,
        date: visit.date,
        label: visit.label,
        subjectId: subject.id,
        subjectLabel: subject.label,
        subjectType: subject.subject_type,
        ageDays: age !== null && Number.isFinite(age) && age >= 0 ? age : null,
        cells,
      });
    }
  }
  return rows;
}
